import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Header from "./Header";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { fr, enUS } from "date-fns/locale";
import Footer from "./Footer";
import ReactConfetti from "react-confetti";
import {startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths, subMonths, startOfWeek, endOfWeek, isSameMonth} from "date-fns";
import { jwtDecode } from "jwt-decode";

const Consultation = () => {
    const { t ,i18n } = useTranslation();
    const currentLocale = i18next.language === 'fr' ? fr : enUS;
    const [consultationTypes, setConsultationTypes] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [assignedCoachName, setAssignedCoachName] = useState(null);
    const [coaches, setCoaches] = useState([]);
    const [selectedCoach, setSelectedCoach] = useState(null);
    const [isCoachLocked, setIsCoachLocked] = useState(false);
    const [availabilities, setAvailabilities] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [showConfetti, setShowConfetti] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);
    const [filteredAvailabilities, setFilteredAvailabilities] = useState([]);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";

    useEffect(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
      const queryParams = new URLSearchParams(location.search);
      const coachFromQuery = queryParams.get("coach"); 
      
      if (coachFromQuery && coaches.length > 0) { 
        const matchingCoach = coaches.find(
          (coach) => `${coach.firstName} ${coach.lastName}` === coachFromQuery
        );
    
        if (matchingCoach) {
          setSelectedCoach(matchingCoach._id); 
        }
      }
    }, [location.search, coaches]);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const isFormValid = firstName && lastName && email && phone && selectedType && selectedCoach && selectedDate && selectedTime;

    useEffect(() => {
      const token = localStorage.getItem("token");
      if (!token) return;
    
      let userId;
      try {
        const decoded = jwtDecode(token);
        userId = decoded.userId;
      } catch (err) {
        console.error("Invalid token:", err);
        return;
      }
    
      fetch(`${API_URL}account/${userId}`)
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch account");
          return res.json();
        })
        .then((data) => {
          setFirstName(data.firstName || "");
          setLastName(data.lastName   || "");
          setEmail(data.email         || "");
          setPhone(data.phoneNumber   || "");
           if (data.assignedCoach) {
            setAssignedCoachName(data.assignedCoach);
          }
        })
        .catch((err) => console.error("Failed to load customer data", err));
    }, [API_URL]);

    useEffect(() => {
  if (assignedCoachName && coaches.length > 0) {
    const match = coaches.find(
      c => `${c.firstName} ${c.lastName}` === assignedCoachName
    );

    if (match) {
      setSelectedCoach(match._id);
      setIsCoachLocked(true);
    } else {
      console.warn(`Assigned coach "${assignedCoachName}" not found in coaches list.`);
    }
  }
}, [assignedCoachName, coaches]);

    const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 });
    const daysInCalendar = eachDayOfInterval({ start, end });

    const handlePrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
    const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1));
    const handleDayClick = (day) => {
        setSelectedDate(day);
        setSelectedTime(null);
    };

    const weekdays = Array.from({ length: 7 }, (_, i) =>
  format(new Date(2021, 0, i + 3), "EEE", { locale: currentLocale })
);

    useEffect(() => {

        fetch(`${API_URL}consultations`)
            .then(res => res.json())
            .then(setConsultationTypes);
    
        fetch(`${API_URL}reps`)
            .then(res => res.json())
            .then(setCoaches);
    }, [API_URL]);

    const isAvailableDay = (day) => {
        const formattedDay = format(day, "yyyy-MM-dd");
        return availabilities.some(av => av.date === formattedDay && av.slots.length > 0);
    };

    const fetchBookedConsultations = async (selectedCoach, selectedDate) => {
      if (!selectedDate) {
        return [];
      }
    
      try {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";
        const response = await fetch(`${API_URL}consultations/booked/${selectedCoach}/${encodeURIComponent(selectedDate)}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
    
        const data = await response.json();
        return data;
      } catch (err) {
        console.error("Error fetching booked consultations:", err);
        return [];
      }
    };
    const filterAvailableSlots = (availabilities, bookedConsultations, consultationType) => {
      const filteredSlots = availabilities.filter((slot) => {
        const slotTime = new Date(slot.time);
        const consultationDuration = consultationType === "1hr" ? 60 : 30;
    
        const isSlotBooked = bookedConsultations.some((consultation) => {
          const bookedTime = new Date(consultation.time);
          const endOfBookedConsultation = new Date(bookedTime.getTime() + consultation.duration * 60000);
    
          return (slotTime >= bookedTime && slotTime < endOfBookedConsultation) || 
                 (slotTime < endOfBookedConsultation && new Date(slotTime.getTime() + consultationDuration * 60000) > bookedTime);
        });
    
        return !isSlotBooked;
      });
    
      return filteredSlots;
    };

    useEffect(() => {
      if (!selectedCoach) return;
    
      const coach = coaches.find(c => c._id === selectedCoach);
      if (!coach) return;
    
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";
      const endpoint = `${API_URL}availabilities/coach/${coach.lastName}`;
    
      fetch(endpoint)
        .then(async res => {
          if (!res.ok) throw new Error("Failed to fetch availabilities");
          const availabilitiesData = await res.json();
          setAvailabilities(availabilitiesData);
    
          try {
            const bookedConsultations = await fetchBookedConsultations(selectedCoach, selectedDate);
            const consultationType = selectedType === "1hr" ? "1hr" : "30min";
            const filteredSlots = filterAvailableSlots(availabilitiesData, bookedConsultations, consultationType);
    
            setFilteredAvailabilities(filteredSlots);
          } catch (err) {
            console.error("Error fetching booked consultations:", err);
          }
        })
        .catch(err => {
          console.error("🚨 Error fetching availabilities:", err);
        });
    }, [selectedCoach, coaches, selectedDate, selectedType]);
    const handleReserve = async () => {
      if (!selectedTime) return;

      setFilteredAvailabilities((prevAvailabilities) => {
        return prevAvailabilities.map((availability) => {
          if (availability.date === format(selectedDate, "yyyy-MM-dd")) {
            return {
              ...availability,
              slots: availability.slots.map((slot) => 
                slot.time === selectedTime ? { ...slot, hidden: true } : slot
              ),
            };
          }
          return availability;
        });
      });

        try {
          const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/";
          const response = await fetch(`${API_URL}reserve`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              firstName,
              lastName,
              email,
              phone,
              coachLN: coaches.find(c => c._id === selectedCoach)?.lastName,
              consultationType: selectedType,
              date: format(selectedDate, "yyyy-MM-dd"),
              time: selectedTime, 
              })
          });
      
          const data = await response.json();

          if (response.ok) {
            setSuccessMessage(t("consultation.successfullyBooked"));
            setShowConfetti(true);
            setIsFading(false);
            setSelectedTime(null);

            await fetch(`${API_URL}send-consultation-email`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                firstName,
                lastName,
                email,
                phone,
                coachLN: coaches.find(c => c._id === selectedCoach)?.lastName,
                consultationType: selectedType,
                realDuration: selectedType === "1hr" ? 60 : 30,
                date: format(selectedDate, "yyyy-MM-dd"),
                time: selectedTime,
              }),
            });
          } else {
            setSuccessMessage(t("consultation.bookingFailed", { error: data.error || "Unknown error" }));
          }
        } catch (error) {
          console.error("Reservation error:", error);
          setSuccessMessage(t("consultation.somethingWentWrong"));
        }
      
        setTimeout(() => setIsFading(true), 5000);
        setTimeout(() => setShowConfetti(false), 7000);
      };

      const handleCoachSelect = (coach) => {
        setSelectedCoach(coach._id);
      };
      const handleTimeSelect = (time) => {
        setSelectedTime(time);
      }

      return (
        <div>
          <Header />
          <Content>
            <Form onSubmit={(e) => e.preventDefault()}>
              <FormTitle>{t("consultation.title")}</FormTitle>
              <FormGroup>
                <label>{t("consultation.firstName")}</label>
                <Input type="text" placeholder={t("consultation.firstName")} value={firstName} onChange={(e) => setFirstName(e.target.value)} required/>
              </FormGroup>
              <FormGroup>
                <label>{t("consultation.lastName")}</label>
                <Input type="text" placeholder={t("consultation.lastName")} value={lastName} onChange={(e) => setLastName(e.target.value)} required/>
              </FormGroup>
              <FormGroup>
                <label>{t("consultation.email")}</label>
                <Input type="email" placeholder={t("consultation.email")} value={email} onChange={(e) => setEmail(e.target.value)} required/>
              </FormGroup>
              <FormGroup>
                <label>{t("consultation.phone")}</label>
                <Input type="tel" placeholder={t("consultation.phone")} value={phone} onChange={(e) => setPhone(e.target.value)} required/>
              </FormGroup>
              <Divider />
              <FormGroup>
  <label>{t("consultation.chooseConsultationType")}</label>
  <DropdownContainer>
    <DropdownHeader onClick={() => setOpen(!open)}>
      {selectedType
        ? consultationTypes.find((type) => type._id === selectedType)?.name[i18n.language] +
          " ~" +
          consultationTypes.find((type) => type._id === selectedType)?.displayDuration +
          " min"
        : `-- ${t("consultation.chooseConsultationType")} --`}
    </DropdownHeader>
    {open && (
      <DropdownList>
        {consultationTypes.map((type) => (
          <DropdownItem key={type._id}
            onClick={() => {
              setSelectedType(type._id);
              setOpen(false);
            }}
          >
            <span><strong>{type.name[i18n.language]}</strong> ~{type.displayDuration} min</span>
            <span><p>{type.slogan[i18n.language]}</p>{type.description[i18n.language]}</span>
            
          </DropdownItem>
        ))}
      </DropdownList>
    )}
  </DropdownContainer>
</FormGroup>

                <FormGroup>
                  <label>{t("consultation.chooseCoach")}</label>
                  <Select 
                  value={selectedCoach || ""}
                  onChange={(e) => {
                      setSelectedCoach(e.target.value);
                      setSelectedDate(null);
                      setSelectedTime(null);
                    }}
                    disabled={isCoachLocked}
                    required
                  >
                    <Option value="">-- {t("consultation.chooseCoach")} --</Option>
                    {coaches
                    .filter(coach => coach.firstName !== "Véronique")
                    .map((coach) => (
                      <Option key={coach._id} value={coach._id} onClick={() => handleCoachSelect(coach.lastName)}>
                        {coach.firstName} {coach.lastName}
                      </Option>
                    ))}
                  </Select>
                </FormGroup>
          
      
              {selectedType && selectedCoach && (
                <CalendarContainer>
                  <MonthHeader>
                    <NavButton onClick={handlePrevMonth}>◀</NavButton>
                    <h2>{format(currentDate, "MMMM yyyy", { locale: currentLocale })}</h2>
                    <NavButton onClick={handleNextMonth}>▶</NavButton>
                  </MonthHeader>
                  
                  <WeekdayHeader>
                    {weekdays.map((day, index) => (
                      <Weekday key={index}>{day}</Weekday>
                    ))}
                  </WeekdayHeader>
                  
                  <DaysGrid>
                    {daysInCalendar.map((day) => (
                      <DayButton key={day} disabled={!isSameMonth(day, currentDate) || !isAvailableDay(day)} onClick={() => handleDayClick(day)}>
                        {format(day, "d")}
                      </DayButton>
                    ))}
                  </DaysGrid>
      
                  {selectedDate && (
                     <TimeSlots>
                     <h3>{format(selectedDate, "EEEE, MMMM d, yyyy", { locale: currentLocale })}</h3>
                     <SlotList>
                       {(filteredAvailabilities.find(av => av.date === format(selectedDate, "yyyy-MM-dd"))?.slots || []).map((slot, index) => (
                         !slot.hidden && (
                          <Slot onClick={() => handleTimeSelect(slot.time)}  key={index}  $isSelected={selectedTime === slot.time}  hidden={slot.hidden}>
                          {slot.time}
                        </Slot>
                         )
                       ))}
                     </SlotList>
      
                      {selectedType && selectedCoach && selectedDate && selectedTime && (
                        <>
                          <p style={{ marginTop: "20px", fontWeight: "bold", color: "#333" }}>
                          {t("consultation.reserve")}<strong>{consultationTypes.find((t) => t._id === selectedType)?.name[i18n.language]}</strong> {t("consultation.with")} <strong>{coaches.find((c) => c._id === selectedCoach)?.firstName} {coaches.find((c) => c._id === selectedCoach)?.lastName}</strong>
                          {t("consultation.on")} <strong>{format(selectedDate, "EEEE, MMMM d, yyyy", { locale: currentLocale })}</strong> {t("consultation.at")} <strong>{selectedTime}</strong>.
                          </p>
                          {isFormValid && (
                          <ReserveButton type="button" onClick={handleReserve}>{t("consultation.reserveButton")}</ReserveButton>
                          )}
                        </>
                      )}
      
                      {successMessage && (
                        <Message error={successMessage.includes("already taken")}>
                          {successMessage.includes("already taken")
                            ? t("consultation.alreadyBooked")
                            : t("consultation.successfullyBooked")}
                        </Message>
                      )}
                    </TimeSlots>
                  )}
                </CalendarContainer>
              )}
      
              {showConfetti && (
                <ConfettiWrapper $fading={isFading}>
                  <ReactConfetti width={window.innerWidth} height={window.innerHeight} />
                </ConfettiWrapper>
              )}
            </Form>
          </Content>
          <Footer />
        </div>
      );
      
};

const CalendarContainer = styled.div`
    margin: 20px auto;
    width: 85%;
    text-align: center;
    border-radius: 10px;
`;
const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 2px dashed #aaa;
  margin: 30px 0 10px;
  padding-bottom: 30px;
`;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
`;

const DropdownHeader = styled.div`
  padding: 12px 15px;
  border: 1px solid gray;
  border-radius: 8px;
  background: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    background: #f5f5f5;
  }
`;

const DropdownList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid gray;
  border-radius: 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 10;
  margin-top: 5px;
  padding: 0;
  list-style: none;
`;

const DropdownItem = styled.li`
  padding: 12px 15px;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: rgba(186,204,171,1);
  }

  strong {
    font-weight: bold;
    font-size: 17px;
    color: #333;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: green;
    font-weight: bold;
    font-style: italic;
  }
`;

const Content = styled.div`
    min-height: 100vh;
    background:linear-gradient(122deg, rgba(148,163,135,1) 0%, rgba(186,204,171,1) 43%, rgba(246,187,155,1) 83%, rgba(217,121,97,1) 100%);
    background-size: cover;
    background-color: rgba(148,163,135,1);
    background-position: top;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-bottom:100px;
    padding-top:70px;
     font-family: 'Montserrat', sans-serif;
  font-weight: 500;
`
const MonthHeader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    font-size: 1.5rem;
`;

const NavButton = styled.button`
    padding: 5px 10px;
    font-size: 1.2rem;
    cursor: pointer;
`;

const WeekdayHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-top: 10px;
    font-weight: bold;
`;

const Weekday = styled.div`
    padding: 5px;
`;

const DaysGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px;
    margin-top: 10px;
`;

const DayButton = styled.button`
    padding: 10px;
    border: 1px solid #ccc;
    background-color: #d4edda;
    cursor: pointer;
    &:disabled {
        background-color: #f0f0f0;
        cursor: not-allowed;
    }
`;

const ReserveButton = styled.button`
background-color: rgba(148,163,135,1);
color: white;
padding:20px;
font-size: 18px;
border: none;
border-radius: 10px;
cursor: pointer;
margin-top: 20px;
&:hover {
    background-color: rgba(186,204,171,1);
  }
`

const TimeSlots = styled.div`
    margin-top: 30px;
`;

const SlotList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;
    margin-top: 10px;
`;

const Slot = styled.div`
  padding: 10px 16px;
  border-radius: 10px;
  margin: 5px;
  text-align: center;
  background-color: ${({ $isSelected }) => ($isSelected ? "#d4edda" : "#ffffff")};
  border: ${({ $isSelected }) => ($isSelected ? "2px solid #28a745" : "1px solid #ccc")};
  color: ${({ $isSelected }) => ($isSelected ? "#155724" : "#333")};
  font-weight: ${({ $isSelected }) => ($isSelected ? "600" : "400")};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: ${({ $isSelected }) => ($isSelected ? "0 2px 6px rgba(0, 128, 0, 0.2)" : "none")};
  opacity: ${({ hidden }) => (hidden ? "0" : "1")}; /* Fade out hidden slots */
  pointer-events: ${({ hidden }) => (hidden ? "none" : "auto")}; /* Prevent clicks on hidden slots */
  
  &:hover {
    background-color: ${({ $isSelected }) => ($isSelected ? "#c3e6cb" : "#f8f9fa")};
  }
`;
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const ConfettiWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  animation: ${(props) => (props.$fading ? fadeOut : "none")} 2s ease-out forwards;
`;

const Input = styled.input`
  padding: 10px 12px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
  transition: border 0.3s;

  &:focus {
    border-color: #4caf50;
    outline: none;
  }
`;
const Select = styled.select`
  padding: 15px;
  font-size: 16px;
  border-radius: 8px;
  width: 275px;
  border: 1px solid gray;
  margin-bottom: 15px;
  background-color: white;
  cursor: pointer;
`;

const Option = styled.option`
  font-size: 16px;
  &:hover {
    cursor: pointer;
  }
`;

const FormTitle = styled.p`
  font-size: 26px;
  text-align: center;
  font-weight: bold;
  color: black;
  margin-bottom: 20px;
`;
const Form = styled.form`
  display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   width: 100%;
   max-width: 1000px;
   padding: 10px;
   background-color: rgba(255, 255, 255, 0.8);
   border-radius: 8px;
   box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;
const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 500px;
  margin-bottom: 15px;

  label {
    margin-bottom: 5px;
    font-weight: bold;
    color: #333;
  }

  input,
  select {
    width: 100%;
  }
`;

const Message = styled.p`
  color: ${(props) => (props.error ? 'red' : 'green')};
  margin-top: 40px;
  font-weight: 600;
  font-size: 25px;
  background-color: ${(props) => (props.error ? '#ffe6e6' : '#e6ffe6')};
  border: 1px solid ${(props) => (props.error ? '#ff4d4d' : '#4dff4d')};
  padding: 8px 15px;
  border-radius: 10px;
`;

export default Consultation;
